import {
  getTaskCheckOption,
  getTaskDayCompletionPercentage,
} from '@griply/utils';
import { isAfter, isSameDay } from 'date-fns';
import React, { useCallback, useMemo, useRef } from 'react';
import { FlagFilled, Repeat12 } from 'shared/assets/icons';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { ID } from 'shared/types/id';
import { Task, TaskCheckOptions } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';

import * as Styled from './event.style';

export type EventTaskProps = {
  task: Task;
  occurrenceId?: ID;
  onComplete: (id: ID, date: Timestamp) => void;
  referenceDate: string;
  weekStartsOn: WeekDays;
  onClick?: (id: ID) => void;
  isDeadline?: boolean;
  color?: string;
  isShort?: boolean;
  marginTop: number;
  lineHeight: number;
  numberOfLines: number;
  onInteractionComplete?: () => void;
};

export const TaskBasicEvent: React.FC<EventTaskProps> = ({
  task,
  occurrenceId,
  referenceDate,
  onComplete,
  weekStartsOn,
  onClick,
  isDeadline,
  color,
  marginTop,
  lineHeight,
  numberOfLines,
  onInteractionComplete,
}) => {
  const today = useToday();
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();

  const containerRef = useRef<HTMLDivElement>(null);

  const refDate = useMemo(() => new Date(referenceDate), [referenceDate]);

  const _onClick = useCallback(
    (e: React.MouseEvent) => {
      // habits and repeating tasks in the future should open the context menu
      if (task.type !== TaskType.Task && !isSameDay(referenceDate, today)) {
        openMenu(e);
        return;
      }

      onClick?.(task.id);
    },
    [onClick, openMenu, referenceDate, task.id, task.type, today],
  );

  const _onComplete = useCallback(() => {
    if (
      task.schedules?.length &&
      !isSameDay(refDate, today) &&
      isAfter(refDate, today)
    ) {
      // do not allow completing habits for future dates
      onClick?.(task.id);
      return;
    }
    onComplete(task.id, refDate);
  }, [onClick, onComplete, refDate, task.id, task.schedules?.length, today]);

  const checked = useMemo(
    () =>
      getTaskCheckOption(task, {
        day: refDate,
        today,
        weekStartsOn,
        showRepeatingAsTodo: true,
      }),
    [refDate, task, today, weekStartsOn],
  );

  const completionPercentage = useMemo(
    () =>
      task.schedules?.length
        ? getTaskDayCompletionPercentage(task, {
            day: refDate,
            weekStartsOn,
          })
        : undefined,
    [refDate, task, weekStartsOn],
  );

  const target = useMemo(() => {
    const activeSchedule = getActiveSchedule(task.schedules, today);
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [task.schedules, today]);

  const isFutureRepeating = useMemo(
    () =>
      task.schedules?.length &&
      !isSameDay(refDate, today) &&
      isAfter(refDate, today),
    [refDate, task.schedules?.length, today],
  );

  const completed =
    checked !== TaskCheckOptions.Unchecked && !isFutureRepeating;

  const isRepeatingIcon =
    isFutureRepeating ||
    [
      TaskCheckOptions.RepeatSingleChecked,
      TaskCheckOptions.RepeatChecked,
    ].includes(checked);

  return (
    <>
      <Styled.Container
        onClick={_onClick}
        onContextMenu={openMenu}
        $color={color}
        $completed={completed}
        ref={containerRef}
      >
        <Styled.Content>
          <Styled.MetaContainer>
            {isRepeatingIcon ? (
              <Styled.RepeatIconContainer $color={color}>
                <Icon icon={Repeat12} />
              </Styled.RepeatIconContainer>
            ) : (
              <TaskDetailCheckbox
                checked={checked}
                name={task.name}
                type={task.type}
                completionPercentage={completionPercentage}
                target={target}
                priority={task.priority}
                onChange={_onComplete}
                color={color}
                isFrozen={!!task.frozenAt}
                isSmall
              />
            )}
          </Styled.MetaContainer>
          <Styled.Title
            $marginTop={marginTop}
            $lineHeight={lineHeight}
            $completed={completed}
            $numberOfLines={numberOfLines}
          >
            {task.name}
          </Styled.Title>
          {isDeadline && (
            <Styled.DeadlineIconContainer $color={color}>
              <Icon icon={FlagFilled} />
            </Styled.DeadlineIconContainer>
          )}
        </Styled.Content>
      </Styled.Container>

      {!!menuPosition && (
        <TaskCardContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          timeslotDate={refDate}
          occurrenceId={occurrenceId}
          onAction={onInteractionComplete}
        />
      )}
      <Tooltip referenceElement={containerRef} label={task.name} delay={400} />
    </>
  );
};
